import type { AdminFormToast } from "./useAdminFormPersistence";

/** The kind of mutation that failed, used to phrase the message. */
export type SaveAction = "save" | "remove" | "add" | "update";

const ACTION_WORDS: Record<SaveAction, { infinitive: string; gerund: string }> =
  {
    save: { infinitive: "save", gerund: "saving" },
    remove: { infinitive: "remove", gerund: "removing" },
    add: { infinitive: "add", gerund: "adding" },
    update: { infinitive: "update", gerund: "updating" },
  };

interface JsonApiError {
  title?: string;
  detail?: string;
  source?: { pointer?: string };
}

const statusOf = (err: unknown): unknown =>
  (err as { status?: unknown } | null)?.status;

const errorsOf = (err: unknown): unknown[] | null => {
  const errors = (err as { data?: { errors?: unknown } } | null)?.data?.errors;
  return Array.isArray(errors) ? errors : null;
};

const capitalizeAttribute = (attribute: string) =>
  attribute.charAt(0).toUpperCase() +
  attribute.slice(1).replaceAll("-", " ").trim();

/** "/data/attributes/external-name" -> "external-name" */
const attributeFromPointer = (pointer?: string): string => {
  const match = /\/data\/attributes\/(.+)$/.exec(pointer ?? "");
  return match ? match[1] : "";
};

/**
 * Splits one JSON:API error into the field it is about and what is wrong.
 *
 * The API sends the field in `detail` ("name - has already been taken") and
 * again in `source.pointer`, while `title` holds the bare message. Prefer the
 * two field-bearing forms so the message names the field the way the legacy
 * Rails forms do; fall back to `title` alone for errors that aren't
 * attribute-scoped (e.g. "is invalid" with an empty detail).
 */
const describeError = (error: JsonApiError): string | null => {
  const detail = error.detail ?? "";
  const separator = detail.indexOf(" - ");
  if (separator !== -1) {
    return withAttribute(
      detail.slice(0, separator),
      detail.slice(separator + 3)
    );
  }

  const title = (error.title ?? "").trim();
  // A bare title reads as a sentence fragment appended to "Could not save X.",
  // so drop a trailing period rather than double up the punctuation.
  const message = title.endsWith(".") ? title.slice(0, -1) : title || detail;
  if (!message) return null;

  return withAttribute(attributeFromPointer(error.source?.pointer), message);
};

const withAttribute = (attribute: string, message: string): string => {
  const label = capitalizeAttribute(attribute);
  return label ? `${label} ${message}` : message;
};

/**
 * The server's own explanation for a failed mutation, or `null` when it didn't
 * send one worth showing.
 *
 * Use this when composing a sentence the `saveErrorToast` wording doesn't fit
 * (e.g. a batch action naming each record that failed). For a single-record
 * save or delete, prefer `saveErrorToast`.
 */
export const saveErrorReason = (err: unknown): string | null => {
  const status = statusOf(err);
  const errors = errorsOf(err);

  // 422 is the only status whose errors are attribute-scoped, and there the
  // message lives in `title` with the field repeated in `detail`. Everywhere
  // else `title` is just the HTTP status name ("Forbidden", "Conflict") and
  // `detail` carries what actually went wrong, so reading them the same way
  // would surface "Conflict" instead of "Record is locked."
  if (status === 422 && errors) {
    const messages = errors
      .map((error) => describeError((error ?? {}) as JsonApiError))
      .filter((message): message is string => !!message);
    return messages.length ? messages.join(", ") : null;
  }

  if (errors) {
    return (errors[0] as { detail?: string } | undefined)?.detail || null;
  }

  return nonJsonApiReason(err);
};

/**
 * The server's explanation for failures that don't arrive as JSON:API - a
 * Rails 500 (`exception`), a plain `{ error }` body, or a bare string.
 *
 * An HTML error page is not a message: Rails serves one for unhandled 500s
 * under some configurations, and pasting markup into a toast is worse than
 * saying nothing.
 */
const nonJsonApiReason = (err: unknown): string | null => {
  const data = (err as { data?: unknown } | null)?.data;

  if (typeof data === "string") {
    const body = data.trim();
    if (!body || body.startsWith("<")) return null;
    return body;
  }

  const body = data as { exception?: unknown; error?: unknown } | null;
  const reason = body?.exception ?? body?.error;
  return typeof reason === "string" && reason.trim() ? reason.trim() : null;
};

/**
 * Maps an RTK Query mutation rejection (from `.unwrap()`) to a danger
 * `AdminFormToast`, so failures surface a meaningful message instead of a
 * silent `console.error`.
 *
 * RTK Query rejects with a `FetchBaseQueryError`: `{ status, data }`, where
 * `status` is the numeric HTTP code for server errors and `data` is the parsed
 * JSON:API body. (This is NOT the axios shape `err.response.status` that the
 * legacy `formatUnprocessibleResponse` expects.)
 *
 * - 403 -> the server's `errors[0].detail` if present, else a fixed message
 *   telling the user their role lacks permission.
 * - 422 -> field-level detail, so messages read like "Could not save approval.
 *   Name can't be blank" or "Could not save business unit. Name has already
 *   been taken" — the same wording the legacy Rails forms show.
 * - anything else (other 4xx/5xx, network/parse errors) -> a generic
 *   "an error occurred" message.
 *
 * @param noun    what was acted on, e.g. "approval" or "approver".
 * @param action  "save" (default), "remove", "add" or "update" — phrases the
 *                message for the kind of mutation that failed.
 */
export const saveErrorToast = (
  err: unknown,
  noun: string,
  action: SaveAction = "save"
): AdminFormToast => {
  const status = statusOf(err);
  const reason = saveErrorReason(err);
  const { infinitive, gerund } = ACTION_WORDS[action];

  if (status === 403) {
    return {
      variant: "danger",
      text:
        reason ||
        `Your role does not have sufficient permissions to ${infinitive} this record.`,
    };
  }

  if (status === 422 && errorsOf(err)) {
    return {
      variant: "danger",
      text: reason
        ? `Could not ${infinitive} ${noun}. ${reason}`
        : `Could not ${infinitive} ${noun}. Please check your input and try again.`,
    };
  }

  // Any other 4xx/5xx: prefer whatever the server actually said over "Please
  // try again", which tells the user nothing and hides a reason that is often
  // sitting right there in the body.
  return {
    variant: "danger",
    text: `An error occurred while ${gerund} the ${noun}. ${
      reason || "Please try again."
    }`,
  };
};

/** One record that a batch action failed on. */
export interface BatchFailure {
  /** How to name the record, when the caller tracked which one failed. */
  label?: string;
  /** The server's reason, from `saveErrorReason`. */
  reason: string | null;
}

/** How many reasons a batch failure message lists before summarising. */
const MAX_REPORTED_FAILURES = 3;

/**
 * Appends the server's reasons to a batch-failure headline, so a batch that
 * failed is actionable rather than a bare count:
 *
 *   "Failed on 1 of 2 business unit(s). Infinity: Name has already been taken"
 *
 * Returns the headline unchanged when the server sent no usable reason.
 * Identical entries collapse (a whole batch usually fails the same way) and
 * long lists are truncated, so the toast stays readable.
 */
export const withFailureReasons = (
  headline: string,
  failures: BatchFailure[]
): string => {
  const reported = failures
    .filter((f) => f.reason)
    .map((f) => (f.label ? `${f.label}: ${f.reason}` : `${f.reason}`))
    .filter((text, i, all) => all.indexOf(text) === i);
  if (reported.length === 0) return headline;

  const shown = reported.slice(0, MAX_REPORTED_FAILURES);
  const remaining = reported.length - shown.length;
  return `${headline} ${shown.join("; ")}${
    remaining > 0 ? `; and ${remaining} more` : ""
  }`;
};

/**
 * Appends the server's reason for a single failed mutation to a message the
 * caller phrases itself — for failures where `saveErrorToast`'s wording does
 * not fit, e.g. one that also tells the user how the page recovered.
 */
export const withErrorReason = (message: string, err: unknown): string =>
  withFailureReasons(message, [{ reason: saveErrorReason(err) }]);
